// src/components/js/ProteinGroupComponent.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../css/UniProtComponent.css';
import {
  FaSearch,
  FaArrowLeft,
  FaArrowRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaTimes,
  FaCopy,
  FaDownload,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ProteinGroupComponent = () => {
  const [proteins, setProteins] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedProtein, setSelectedProtein] = useState(null);
  const [modalType, setModalType] = useState('');
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all'); // all / reviewed / unreviewed
  const [selectedProteins, setSelectedProteins] = useState(new Set());

  const limit = 20;
  const navigate = useNavigate();

  // Fetch proteins
  useEffect(() => {
    const fetchProteins = async () => {
      setLoading(true);
      try {
        let url = `http://197.156.76.122/api/proteins/?page=${page}`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        if (reviewFilter === 'reviewed') url += `&reviewed=true`;
        if (reviewFilter === 'unreviewed') url += `&reviewed=false`;

        const res = await axios.get(url);
        setProteins(res.data.results);
        setTotal(res.data.count);
      } catch (error) {
        console.error('Error fetching proteins:', error);
      }
      setLoading(false);
    };
    fetchProteins();
  }, [page, searchQuery, reviewFilter]);

  const handleShowModal = (protein, type) => {
    setSelectedProtein(protein);
    setModalType(type);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = (data, filename, type = "text/plain") => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const handleDownload = (format) => {
  const list =
    selectedProteins.size > 0
      ? proteins.filter(p => selectedProteins.has(p.accession))
      : proteins;

  if (!list.length) return;

  // ===================== FASTA =====================
  if (format === "fasta") {
    const data = list
      .map(p => `>${p.accession} ${p.protein_name || ""}\n${p.sequence || ""}`)
      .join("\n");

    downloadFile(data, "proteins.fasta");
  }

  // ===================== JSON =====================
  if (format === "json") {
    downloadFile(JSON.stringify(list, null, 2), "proteins.json");
  }

  // ===================== XML =====================
  if (format === "xml") {
    const data = list
      .map(p =>
        p.xml_data && Object.keys(p.xml_data).length
          ? JSON.stringify(p.xml_data, null, 2)
          : ""
      )
      .filter(Boolean)
      .join("\n");

    downloadFile(data || "<xml>Not available</xml>", "proteins.xml");
  }

  // ===================== GFF3 =====================
  if (format === "gff3") {
    const data = list
      .map(p =>
        p.gff3_features?.length
          ? JSON.stringify(p.gff3_features, null, 2)
          : ""
      )
      .filter(Boolean)
      .join("\n");

    downloadFile(data || "", "proteins.gff3");
  }
};

  const totalPages = Math.ceil(total / limit);

  const PaginationControls = () => (
    <div className="pagination-controls">
      <button onClick={() => setPage(1)} disabled={page === 1}><FaAngleDoubleLeft /> First</button>
      <button onClick={() => setPage(prev => prev - 1)} disabled={page === 1}><FaArrowLeft /> Prev</button>
      <span className="pagination-status">Page {page} of {totalPages}</span>
      <button onClick={() => setPage(prev => prev + 1)} disabled={page === totalPages}>Next <FaArrowRight /></button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last <FaAngleDoubleRight /></button>
    </div>
  );

  const getUniProtDetailLink = (protein) => `https://www.uniprot.org/uniprot/${protein.accession}`;

  const toggleProteinSelection = (accession) => {
    setSelectedProteins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accession)) newSet.delete(accession);
      else newSet.add(accession);
      return newSet;
    });
  };

  const getNonEmptyAttributes = (protein) => {
    const attributes = {
      Accession: protein.accession,
      'Entry Name': protein.entry_name,
      'Protein Name': protein.protein_name,
      'Gene Names': protein.gene_names?.length ? protein.gene_names.join(', ') : undefined,
      Organism: protein.organism,
      'Tax ID': protein.tax_id,
      Length: protein.sequence_length,
      'Sequence Version': protein.sequence_version,
      'EC Numbers': protein.ec_numbers?.length ? protein.ec_numbers.join(', ') : undefined,
      Keywords: protein.keywords?.length ? protein.keywords.join(', ') : undefined,
      Isoforms: protein.isoforms?.length ? JSON.stringify(protein.isoforms, null, 2) : undefined,
      'GFF3 Features': protein.gff3_features?.length ? JSON.stringify(protein.gff3_features, null, 2) : undefined,
      'Xrefs': protein.xrefs?.length ? JSON.stringify(protein.xrefs, null, 2) : undefined,
      Comments: protein.comments?.length ? JSON.stringify(protein.comments, null, 2) : undefined,
      'Created Date': protein.created_date,
      'Modified Date': protein.modified_date,
      'Dataset': protein.dataset,
      'FASTA Header': protein.fasta_header,
      Sequence: protein.sequence,
      'XML Data': protein.xml_data && Object.keys(protein.xml_data).length ? JSON.stringify(protein.xml_data, null, 2) : undefined,
    };

    return Object.entries(attributes).filter(([_, v]) => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
      return true;
    });
  };

  return (
    <div className="protein-group-container">

      {/* 🔙 Back to Home Button */}
      <div className="pci-back-wrapper">
        <button className="pci-back-home" onClick={() => navigate('/')}>
          <FaArrowLeft className="pci-back-icon" /> Back to Home
        </button>
      </div>

      <h2>Protein Groups</h2>

      {/* Search & Filter */}
      <div className="search-filter-bar">
        <input
          type="text"
          placeholder="Search by accession, name, or title..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />

        <select value={reviewFilter} onChange={e => setReviewFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="reviewed">Reviewed</option>
          <option value="unreviewed">Unreviewed</option>
        </select>

        <div className="download-dropdown">
          <button className="download-btn"><FaDownload /> Download ▼</button>
          <div className="dropdown-content">
            {proteins.some(p => p.sequence) && <span onClick={() => handleDownload('fasta')}>FASTA</span>}
            {proteins.length && <span onClick={() => handleDownload('json')}>JSON</span>}
            {proteins.some(p => p.xml_data && Object.keys(p.xml_data).length) && <span onClick={() => handleDownload('xml')}>XML</span>}
            {proteins.some(p => p.gff3_features?.length) && <span onClick={() => handleDownload('gff3')}>GFF3</span>}
          </div>
        </div>
      </div>

      <p className="results-summary">
        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} proteins
      </p>

      <PaginationControls />

      {loading ? (
        <div className="loading-spinner">Loading proteins...</div>
      ) : (
        <div className="protein-list">
          {proteins.map((protein, index) => (
            <div key={protein.accession} className="protein-card">
              <div className="protein-header">
                <input
                  type="checkbox"
                  checked={selectedProteins.has(protein.accession)}
                  onChange={() => toggleProteinSelection(protein.accession)}
                  style={{ marginRight: "10px" }}
                />
                <strong>
                  <span
                    className="protein-link"
                    onClick={() => handleShowModal(protein, 'title')}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {protein.protein_name || protein.accession}
                  </span>
                </strong>
              </div>
              <span className="protein-index">
                {(page - 1) * limit + index + 1}. <strong>Accession:</strong> {protein.accession} | <strong>Length:</strong> {protein.sequence_length} aa
              </span>
              <div className="protein-actions">
                {protein.sequence && <button className="btn-fasta" onClick={() => handleShowModal(protein, 'fasta')}>FASTA</button>}
                <button className="btn-blast" onClick={() => navigate(`/blast?sequence=${protein.accession}`)}><FaSearch /> BLAST</button>
                <a
                  href={getUniProtDetailLink(protein)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-link-btn"
                  title="View UniProt entry"
                >
                  <FaExternalLinkAlt /> UniProt
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <PaginationControls />

      {modalType && selectedProtein && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalType === 'fasta' ? `FASTA: ${selectedProtein.accession}` : selectedProtein.protein_name || selectedProtein.accession}
              </h3>
              <div className="modal-actions">
                <button className="copy-btn" onClick={() => handleCopy(
                  modalType === 'fasta'
                    ? `>${selectedProtein.accession} ${selectedProtein.protein_name}\n${selectedProtein.sequence}`
                    : JSON.stringify(selectedProtein, null, 2)
                )}>
                  <FaCopy /> {copied ? 'Copied!' : 'Copy'}
                </button>

                <div className="download-dropdown">
                  <button className="download-btn"><FaDownload /> Download ▼</button>
                  <div className="dropdown-content">
                    {selectedProtein.sequence && <span onClick={() => handleDownload('fasta')}>FASTA</span>}
                    <span onClick={() => handleDownload('json')}>JSON</span>
                    {selectedProtein.xml_data && Object.keys(selectedProtein.xml_data).length > 0 && <span onClick={() => handleDownload('xml')}>XML</span>}
                    {selectedProtein.gff3_features && selectedProtein.gff3_features.length > 0 && <span onClick={() => handleDownload('gff3')}>GFF3</span>}
                  </div>
                </div>

                <button className="close-btn" onClick={() => setModalType('')}><FaTimes /></button>
              </div>
            </div>

            <div className="modal-body">
              {modalType === 'fasta' ? (
                <pre className="sequence-display">
                  {`>${selectedProtein.accession} ${selectedProtein.protein_name}\n${selectedProtein.sequence}`}
                </pre>
              ) : (
                <div className="metadata-display">
                  <h4>Metadata</h4>
                  <ul>
                    {getNonEmptyAttributes(selectedProtein).map(([key, value]) => (
                      <li key={key}><strong>{key}:</strong> {value}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProteinGroupComponent;
